import {
    NetworkError,
    generateRouteDefinition,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import {
    makeLibraryPlaylistPageIntent,
    type LibraryPlaylistPageIntent,
    makePlaylistPaginationIntent,
} from './LibraryPlaylistPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { LibraryPlaylistPage } from '@jet-app/music-classical/models/pages/library/playlist';
import { IntentKind } from '@jet-app/music-classical/intents';
import type { PaginationIntent } from '@jet-app/music-classical/intents/common/pagination-invalidation-intent';
import { renderPlaylistPage } from '../../playlist-page/renderPlaylistPage';
import { requestViewPage } from '@jet-app/music-classical/api/request-view-page';
import type {
    ClassicalListViewPage,
    ClassicalSection,
    ClassicalPaginatedResponse,
    ClassicalShelfItem,
} from '@jet-app/music-classical/api/types';
import {
    isEmptyPage,
    renderEmptyPage,
} from '@jet-app/music-classical/utilities/transforms/empty-view';
import { makeRefreshPageIntent } from '@jet-app/music-classical/intents/refresh-page/RefreshPageIntent';
import { requiresSignIn } from '../utils/requires-sign-in';
import {
    getFooterTrackMetadata,
    tracklistDescription,
} from '@jet-app/music-classical/utilities/transforms/playlist';

const canonicalUrlRule = {
    path: '/library/playlist/{id}',
    query: [],
};

export const LibraryPlaylistPageIntentController: IntentController<LibraryPlaylistPageIntent> &
    RouteProvider = requiresSignIn({
    $intentKind: IntentKind.LIBRARY_PLAYLIST_PAGE,

    routes: generateRouteDefinition<LibraryPlaylistPageIntent>(
        canonicalUrlRule,
        makeLibraryPlaylistPageIntent,
        { isLibrary: true },
    ),

    async perform(
        intent: LibraryPlaylistPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<LibraryPlaylistPage> {
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        const apiResponse = await requestViewPage(
            `library-playlist/${intent.id}`,
            objectGraph,
        );

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        if (isEmptyPage(apiResponse)) {
            // Overriding the icon as it is not returned by server
            (apiResponse.body as ClassicalListViewPage).firstPage.icon =
                'track';
            return renderEmptyPage<LibraryPlaylistPage>(
                canonicalURL,
                apiResponse,
                objectGraph,
            );
        }

        // Used to create the track list section as well as calculate footer details
        const listViewPage = apiResponse.body as ClassicalListViewPage;
        const paginatedResponse =
            listViewPage.firstPage as ClassicalPaginatedResponse;
        const trackListItems = paginatedResponse?.items as ClassicalShelfItem[];
        const nextPageURL = paginatedResponse?.next;

        apiResponse.body.sections =
            createClassicalAPITracklistSection(trackListItems);

        const trackListFooterSection = createClassicalAPIFooterSection(
            trackListItems,
            objectGraph,
            !!nextPageURL,
        );

        apiResponse.body.sections.push(trackListFooterSection);

        const page = renderPlaylistPage(
            canonicalURL,
            apiResponse,
            undefined,
            objectGraph,
            undefined,
            true,
        ) as unknown as LibraryPlaylistPage;

        return {
            ...page,
            nextIntent: nextPageURL
                ? makePlaylistPaginationIntent({
                      url: nextPageURL,
                      id: intent.id,
                  } as Partial<PaginationIntent>)
                : null,
            invalidationRules: {
                eventTriggers: [
                    {
                        events: [{ name: 'libraryPlaylistUpdated' }],
                        intent: makeRefreshPageIntent(
                            intent,
                            'Library Playlist Updated',
                        ),
                    },
                ],
            },
        };
    },
});

export function createClassicalAPITracklistSection(
    items: ClassicalShelfItem[],
): ClassicalSection[] {
    return [
        {
            priority: 'primary',
            type: 'track-list',
            components: [
                {
                    type: 'list',
                    items,
                },
            ],
        },
    ];
}

export function createClassicalAPIFooterSection(
    items: ClassicalShelfItem[],
    objectGraph: ObjectGraph,
    hasNextPage: boolean = false,
): ClassicalSection {
    const meta = getFooterTrackMetadata(items);
    const trackListFooterComponent = {
        type: 'list',
        items: [
            {
                type: 'text',
                text: tracklistDescription(
                    meta.count,
                    meta.duration,
                    objectGraph,
                ),
                style: 'trackListFootNote',
                hidden: hasNextPage,
                meta,
            },
        ],
    };

    const trackListFooterSection: ClassicalSection = {
        type: 'track-list-footer',
        priority: 'primary',
        components: [trackListFooterComponent],
    };

    return trackListFooterSection;
}
